// Two brands are covered — Rabipur® (1.0 mL) and Verorab® (0.5 mL). Three
// schedules are provided: standard (days 0, 7, 28), accelerated (days 0,
// 3, 7 — Rabipur® only, off-label outside 18–65), and one-week 2-dose
// (days 0 and 7, immunocompetent only, serology required). The
// accelerated schedule is labelled to indicate it is Rabipur® only — the
// pharmacist must not use Verorab® on the accelerated schedule. This is a
// known limitation of the current config type (no brand-to-schedule
// link); documented here as a code comment only.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.RABIES,
  displayName: "Rabies pre-exposure prophylaxis",
  serviceType: "administration",
  minimumAgeYears: 0,
  indicationSummary:
    "Living in or travelling to a rabies-enzootic area where NaTHNaC assessment indicates pre-exposure prophylaxis is recommended. Pre-exposure vaccination reduces but does not eliminate the risk of rabies and does NOT remove the need for post-exposure treatment after a potential exposure.",

  brands: [
    {
      brandId: "rabipur",
      brandName: "Rabipur\u00ae",
      minimumAgeYears: 0,
      route: "IM",
      doseDescription: "1.0 mL per dose (reconstituted)",
      administrationNotes:
        "Reconstitute the freeze-dried vaccine with the supplied solvent immediately before use. Gently swirl until dissolved \u2014 do not shake vigorously. Inspect the reconstituted solution \u2014 it should be clear and colourless to slightly yellowish. Use immediately after reconstitution. Administer by intramuscular injection into the deltoid muscle. In infants and young children, administer into the anterolateral thigh. Never administer intravenously, subcutaneously, or intradermally.",
    },
    {
      brandId: "verorab",
      brandName: "Verorab\u00ae",
      minimumAgeYears: 0,
      route: "IM",
      doseDescription: "0.5 mL per dose (reconstituted)",
      administrationNotes:
        "Reconstitute the freeze-dried vaccine with the supplied solvent immediately before use. Gently swirl until dissolved. Inspect the reconstituted solution \u2014 it should be clear and colourless. Use immediately after reconstitution. Administer by intramuscular injection into the deltoid muscle. In infants and young children, administer into the anterolateral thigh. Note: the syringe tip caps of Verorab\u00ae contain a latex derivative \u2014 check for latex allergy before use.",
    },
  ],

  // Known limitation: the accelerated schedule is Rabipur® only, but the
  // current TravelHealthTier2Config type has no brand-to-schedule link.
  // The schedule label explicitly states Rabipur® only. The pharmacist
  // must select the correct schedule for their chosen brand.
  schedules: [
    {
      scheduleId: "rabies-standard",
      label: "Standard pre-exposure schedule (days 0, 7, and 28)",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose",
        },
        {
          doseNumber: 2,
          label: "Day 7 \u2014 second dose",
          intervalFromDose1Days: 7,
        },
        {
          doseNumber: 3,
          label: "Day 28 \u2014 third dose (may be given from day 21 if time is short)",
          intervalFromDose1Days: 28,
        },
      ],
      boosterInfo:
        "Boosters are not given routinely. Consider a booster for patients who completed a primary course more than 1 year ago and are travelling to a high-risk area, based on individual risk assessment or serology.",
      completionNotes:
        "Preferred schedule for all eligible patients. Third dose may be brought forward to day 21 if time before travel is short. Complete the course before potential exposure where possible.",
    },
    {
      scheduleId: "rabies-accelerated",
      label:
        "Accelerated schedule (days 0, 3, and 7) \u2014 Rabipur\u00ae ONLY \u2014 licensed for adults 18\u201365; off-label for other ages",
      offLabel: true,
      offLabelRationale:
        "The accelerated Rabipur\u00ae schedule (days 0, 3, and 7) is licensed for adults aged 18\u201365 where there is insufficient time for the standard schedule. Its use in patients aged 6 months to 17 years and in adults aged 65 and over is outside the product licence, supported by Green Book guidance where travel is imminent. This schedule is for Rabipur\u00ae only \u2014 do not use Verorab\u00ae on this schedule. Off-label use requires documented rationale and patient consent.",
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose",
        },
        {
          doseNumber: 2,
          label: "Day 3 \u2014 second dose",
          intervalFromDose1Days: 3,
        },
        {
          doseNumber: 3,
          label: "Day 7 \u2014 third dose",
          intervalFromDose1Days: 7,
        },
      ],
      boosterInfo:
        "A further dose at 1 year is recommended for patients at continued risk following the accelerated primary course.",
      completionNotes:
        "Rabipur\u00ae only. Off-label for ages outside 18\u201365 \u2014 patient consent must be documented. A further dose at 1 year is recommended for continued-risk patients.",
    },
    {
      scheduleId: "rabies-one-week-two-dose",
      label:
        "One-week 2-dose schedule (days 0 and 7) \u2014 immunocompetent patients only; serology required 2\u20134 weeks after dose 2",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose",
        },
        {
          doseNumber: 2,
          label: "Day 7 \u2014 second dose",
          intervalFromDose1Days: 7,
        },
      ],
      completionNotes:
        "For immunocompetent patients only. A blood test to confirm adequate rabies virus neutralising antibody titre (RVNA \u22650.5 IU/mL) must be arranged 2\u20134 weeks after the second dose. Document that the patient has been advised to arrange this serology. For Verorab\u00ae: serology is recommended routinely 2\u20134 weeks after completing any primary course.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "rabies-ex-anaphylaxis",
      question:
        "Has the patient had a confirmed anaphylactic reaction to a previous dose of a rabies vaccine or to any of its components?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Rabies vaccine is contraindicated in this patient. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "rabies-ex-neomycin",
      question: "Does the patient have a severe allergy to neomycin?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Both Rabipur\u00ae and Verorab\u00ae contain neomycin residuals. This vaccine is contraindicated in patients with severe neomycin allergy. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "rabies-ex-occupational-only",
      question:
        "Is the patient's sole reason for seeking vaccination an occupational risk of rabies (i.e. no travel or personal lifestyle risk \u2014 laboratory, veterinary, or animal-handling work only)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Occupational vaccination against rabies is outside this PGD. Do not supply under this PGD.",
      referralMessage:
        "Direct the patient to their employer's occupational health service as the primary route for occupational rabies vaccination.",
    },
  ],

  cautions: [
    {
      checkId: "rabies-caut-bleeding",
      question:
        "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "Administer by intramuscular injection with care using a fine-gauge needle with firm pressure applied for at least two minutes afterwards. For patients on stable warfarin with INR within therapeutic range, IM injection may proceed. Document the clinical decision.",
    },
    {
      checkId: "rabies-caut-pregnancy",
      question: "Is the patient pregnant or breastfeeding?",
      cautionMessage:
        "Rabies pre-exposure vaccination should proceed in pregnancy only where the benefit of protection against rabies clearly outweighs any theoretical risk. If high-risk travel is unavoidable, vaccination may proceed after a documented risk-benefit discussion. Advise the patient to inform their GP or midwife. Breastfeeding is not a contraindication.",
    },
    {
      checkId: "rabies-caut-immunosuppressed",
      question: "Is the patient immunosuppressed due to disease or treatment?",
      cautionMessage:
        "Immunosuppression may reduce the immune response to rabies vaccine. Vaccination should still proceed. For Verorab\u00ae: check antibody response (RVNA) 2\u20134 weeks after completing the primary course to confirm adequate protection. Counsel that additional doses may be needed. For Rabipur\u00ae: consider serology if immunosuppression is significant.",
    },
    {
      checkId: "rabies-caut-hydroxychloroquine",
      question: "Is the patient currently taking or planning to take hydroxychloroquine?",
      cautionMessage:
        "Hydroxychloroquine is predicted to reduce rabies vaccine efficacy. Check the current BNF for the latest interaction guidance. Consider the timing of hydroxychloroquine relative to vaccination or check antibody response (RVNA) 2\u20134 weeks after completing the course.",
    },
    {
      checkId: "rabies-caut-syncope",
      question:
        "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccination gives you pre-exposure protection against rabies. It reduces the risk of developing rabies after an exposure but does NOT mean you can skip post-exposure treatment after a potential exposure.",
    "Avoid contact with wild and domestic animals, particularly dogs, cats, bats, and monkeys.",
    "If you are bitten, scratched, or licked on broken skin by any animal: wash the wound immediately with soap and running water for at least 5 minutes, apply an antiseptic such as iodine solution or 40\u201370% alcohol, and seek medical attention urgently \u2014 even if you have been vaccinated. Tell the clinician you have had pre-exposure rabies vaccination.",
    "Pre-exposure vaccination does not remove the need for post-exposure treatment (additional doses of rabies vaccine and possibly rabies immunoglobulin). Always seek medical care after a potential exposure.",
    "A booster dose may be needed if you remain at risk \u2014 your pharmacist or travel health provider will advise you on timing.",
    "If you received the one-week 2-dose schedule: a blood test to confirm adequate protection must be arranged 2\u20134 weeks after your second dose. Please ensure you arrange this test.",
  ],
});
